<template>
  <ValidationObserver rules="required" v-slot="{ handleSubmit }">
    <RightSideBar
      v-if="isOpen"
      @close="$emit('close')"
      @submit="handleSubmit(handleSave)"
      submit="Save"
      cancel="Cancel"
      button-class="bg-dynamicBackBtn text-white"
      padding="padding: 0 1rem"
      :loading="loading"
      :disabled-button="loading"
    >
      <template v-slot:title>
        <div class="flex items-center text-darkPurple w-full">
          <back-button
            variant="secondary"
            @onClick="$emit('close')"
            class="mr-3"
          />
          <h2 class="font-bold text-lg">{{ drawerTitle }}</h2>
        </div>
      </template>

      <div class="pt-1 text-darkPurple">
        <div class="mb-4">
          <c-text
            :label="titleLabel"
            placeholder="--Enter--"
            variant="w-full h-10"
            v-model="form.title"
            :rules="['required']"
          />
        </div>

        <template v-if="showPostTypeField">
          <div class="mb-4">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Post Type"
              :options="postTypeFieldOptions"
              v-model="form.postType"
              :rules="requiredRules"
            />
          </div>
        </template>

        <template v-if="showJobClassificationField">
          <div class="mb-4">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              :label="isJobLevel ? 'Job Family (Optional)' : 'Job Cadre'"
              :options="jobClassifications"
              v-model="form.jobClassificationId"
              :rules="jobClassificationRules"
            />
          </div>
        </template>

        <template v-if="isJobLevel && showRoleFields">
          <div class="mb-4">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Job Level"
              :options="levels"
              v-model="form.jobLevelId"
              :rules="requiredRules"
            />
          </div>
          <div class="mb-4">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Job Grade (Optional)"
              :options="jobGrades"
              v-model="form.jobGradeId"
            />
          </div>
          <div class="mb-4">
            <p class="label mb-1">Compensation Model</p>
            <div class="readonly-field">
              {{ formattedCompensationModel }}
            </div>
          </div>
        </template>

        <template v-if="!isJobLevel && showPostFields">
          <div class="mb-4">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Minimum Grade Level (Optional)"
              :options="levels"
              v-model="form.minimumJobLevelId"
            />
          </div>
          <div class="mb-4">
            <label class="flex">Maximum Grade Level (Optional)</label>
            <v-select
              :clearable="false"
              class="style-select"
              placeholder="--Select--"
              :reduce="(option) => option.id"
              :options="maxLevelOptions"
              v-model="form.maximumJobLevelId"
              :selectable="(option) => !option.disabled"
              :filterable="false"
              :searchable="false"
            >
              <template #option="item">
                <div>
                  <span
                    :class="item.disabled ? 'text-romanSilver' : ''"
                    class="font-bold text-base"
                  >{{ item.name }}</span>
                </div>
              </template>
              <template #selected-option="{ name }">
                <span class="overflow-hidden">{{ name }}</span>
              </template>
              <template #open-indicator="{ attributes }">
                <span v-bind="attributes">
                  <icon icon-name="caret-down" size="xsm" />
                </span>
              </template>
            </v-select>
          </div>
          <div class="mb-4">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Minimum Step (Optional)"
              :options="jobGrades"
              v-model="form.minimumJobGradeId"
            />
          </div>
          <div class="mb-4">
            <p class="label mb-1">Compensation Model</p>
            <div class="readonly-field">
              {{ formattedCompensationModel }}
            </div>
          </div>
        </template>

        <template v-if="showAdministrativeFields">
          <div class="mb-4">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Role category"
              :options="roleCategoryOptions"
              v-model="form.category"
              :rules="requiredRules"
            />
          </div>
          <div class="mb-4">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Appointment nature"
              :options="appointmentNatureOptions"
              v-model="form.appointmentNature"
              :rules="requiredRules"
            />
          </div>
          <div class="mb-4">
            <div class="grid grid-cols-12 gap-2">
              <div class="col-span-6">
                <c-text
                  label="Appointment Duration"
                  placeholder="--Enter--"
                  type="number"
                  variant="w-full h-10"
                  v-model="form.appointmentTenureValue"
                  :rules="appointmentTenureValueRules"
                />
              </div>
              <div class="col-span-6">
                <c-select
                  placeholder="--Select--"
                  label="unit"
                  variant="w-full"
                  :options="appointmentTenureUnitOptions"
                  v-model="form.appointmentTenureUnit"
                  :rules="appointmentTenureUnitRules"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </RightSideBar>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import RightSideBar from "@/components/RightSideBar";
import VSelect from "vue-select";
import "vue-select/dist/vue-select.css";

const emptyForm = () => ({
  title: "",
  postType: null,
  jobClassificationId: null,
  jobLevelId: null,
  jobGradeId: null,
  minimumJobLevelId: null,
  maximumJobLevelId: null,
  minimumJobGradeId: null,
  category: "",
  appointmentNature: "",
  appointmentTenureValue: "",
  appointmentTenureUnit: null,
});

export default {
  name: "NonPositionDrawer",
  components: {
    ValidationObserver,
    CText,
    CSelect,
    BackButton,
    RightSideBar,
    VSelect,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    activeTab: {
      type: String,
      default: "",
    },
    isJobLevel: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: null,
    },
    jobClassifications: {
      type: Array,
      default: () => [],
    },
    levels: {
      type: Array,
      default: () => [],
    },
    jobGrades: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    compensationModel: {
      type: String,
      default: "",
    },
    isEducationalInstitution: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: emptyForm(),
      roleCategoryOptions: [
        { id: "Administrative Head", name: "Administrative Head" },
        { id: "Principal Officer", name: "Principal Officer" },
      ],
      appointmentNatureOptions: [
        { id: "substantive", name: "Substantive" },
        { id: "concurrent", name: "Concurrent" },
      ],
      appointmentTenureUnitOptions: [
        { id: "years", name: "Years" },
        { id: "months", name: "Months" },
      ],
    };
  },
  computed: {
    drawerTitle() {
      const action = this.isEdit ? "Edit" : "New";
      if (this.activeTab === "Role") return `${action} Role`;
      if (this.activeTab) return `${action} ${this.activeTab}`;
      return `${action} ${this.isJobLevel ? "Role" : "Post"}`;
    },
    titleLabel() {
      return this.isJobLevel ? "Role title" : "Post title";
    },
    showRoleFields() {
      return this.activeTab === "Role";
    },
    showPostFields() {
      return (
        this.activeTab === "Post" ||
        this.activeTab === "Academic Post" ||
        this.activeTab === "Non-Academic Post"
      );
    },
    showPostTypeField() {
      return this.activeTab === "Post" && this.isEducationalInstitution;
    },
    postTypeFieldOptions() {
      return [
        { id: "Academic Post", name: "Academic post" },
        { id: "Non-Academic Post", name: "Non-Academic post" },
      ];
    },
    showAdministrativeFields() {
      return this.activeTab === "Administrative Post";
    },
    showJobClassificationField() {
      return this.showRoleFields || this.showPostFields;
    },
    maxLevelOptions() {
      if (!this.form.minimumJobLevelId) {
        return this.levels;
      }

      const selectedIndex = this.levels.findIndex(
        (item) => item.id === this.form.minimumJobLevelId
      );

      return this.levels.map((item, index) => ({
        ...item,
        disabled: index >= selectedIndex,
      }));
    },
    formattedCompensationModel() {
      const labels = {
        rangeBased: "Range Based",
        scaleBased: "Scale Based",
      };

      if (!this.compensationModel) {
        return "------";
      }

      return labels[this.compensationModel] || this.compensationModel;
    },
    requiredRules() {
      return ["required"];
    },
    jobClassificationRules() {
      return this.showPostFields ? ["required"] : [];
    },
    appointmentTenureValueRules() {
      return this.showAdministrativeFields ? ["required"] : [];
    },
    appointmentTenureUnitRules() {
      return this.showAdministrativeFields ? ["required"] : [];
    },
  },
  watch: {
    isOpen(value) {
      if (!value) {
        this.form = emptyForm();
        return;
      }
      this.populateForm();
    },
    initialData: {
      deep: true,
      handler() {
        if (this.isOpen) {
          this.populateForm();
        }
      },
    },
    "form.minimumJobLevelId"(newVal) {
      if (
        this.form.maximumJobLevelId &&
        newVal &&
        this.form.maximumJobLevelId === newVal
      ) {
        this.form.maximumJobLevelId = null;
      }
    },
  },
  methods: {
    populateForm() {
      const data = this.initialData || {};
      this.form = {
        title: data.title || "",
        postType: this.resolvePostType(data),
        jobClassificationId:
          data.jobClassificationId || data.jobClassification?.id || null,
        jobLevelId: data.jobLevelId || data.jobLevel?.id || null,
        jobGradeId: data.jobGradeId || data.jobGrade?.id || null,
        minimumJobLevelId:
          data.minimumJobLevelId || data.minimumJobLevel?.id || null,
        maximumJobLevelId:
          data.maximumJobLevelId || data.maximumJobLevel?.id || null,
        minimumJobGradeId:
          data.minimumJobGradeId || data.minimumJobGrade?.id || null,
        category: data.category || "",
        appointmentNature: data.appointmentNature || "",
        ...this.parseAppointmentTenure(data.appointmentTenure),
      };
    },
    parseAppointmentTenure(tenure) {
      if (!tenure) {
        return {
          appointmentTenureValue: "",
          appointmentTenureUnit: null,
        };
      }

      const match = String(tenure).match(/^(\d+(?:\.\d+)?)\s*(years|months)$/i);
      if (match) {
        return {
          appointmentTenureValue: match[1],
          appointmentTenureUnit: match[2].toLowerCase(),
        };
      }

      return {
        appointmentTenureValue: tenure,
        appointmentTenureUnit: null,
      };
    },
    formatAppointmentTenure() {
      const { appointmentTenureValue, appointmentTenureUnit } = this.form;

      if (!appointmentTenureValue && !appointmentTenureUnit) {
        return null;
      }

      if (!appointmentTenureValue) {
        return appointmentTenureUnit;
      }

      if (!appointmentTenureUnit) {
        return appointmentTenureValue;
      }

      return `${appointmentTenureValue} ${appointmentTenureUnit}`;
    },
    resolvePostType(data) {
      if (this.activeTab === "Academic Post") {
        return "Academic Post";
      }
      if (this.activeTab === "Non-Academic Post") {
        return "Non-Academic Post";
      }

      const typeMap = {
        academic: "Academic Post",
        "non-academic": "Non-Academic Post",
      };

      return typeMap[data.type] || data.postType || null;
    },
    handleSave() {
      const {
        appointmentTenureValue,
        appointmentTenureUnit,
        postType,
        ...formData
      } = this.form;

      this.$emit("save", {
        ...formData,
        postType,
        appointmentTenure: this.formatAppointmentTenure(),
        id: this.initialData?.id || null,
        isEdit: this.isEdit,
      });
    },
  },
};
</script>

<style scoped>
.label {
  font-size: 14px;
  color: #2d3142;
}

.readonly-field {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 4px;
  background: #eeeeee;
  color: #321c3b;
  font-size: 14px;
}
</style>
